/**
 * Dictation API slice — speech-to-text for the chat composer.
 * Leaf module (Perf 3.1.b/e Step 6). Pulls only the shared chokepoint from
 * ./client; never imports a sibling @/lib/api slice.
 */
import { fetchWithAuth, ApiError } from './client';

// Five-minute dictations can take longer to upload and transcribe on mobile.
const DICTATE_TIMEOUT_MS = 2 * 60 * 1000;

async function fetchDictation(endpoint, options) {
  try {
    return await fetchWithAuth(endpoint, options);
  } catch (error) {
    if (error.status === 403 && error.data?.code === 'NON_JSON_RESPONSE') {
      const requestId = error.data.requestId;
      const reference = typeof requestId === 'string' && /^[a-zA-Z0-9_-]{1,100}$/.test(requestId)
        ? ` Reference: ${requestId}.` : '';
      throw new ApiError(
        'Dictation upload was blocked (403). Please try again. If it continues, contact support.' + reference,
        error.status,
        error.data,
      );
    }
    throw error;
  }
}

export const dictateApi = {
  // Transcribe audio to text using Whisper (web — sends a Blob)
  transcribe: async (audioBlob, filename = 'recording.webm') => {
    const formData = new FormData();

    // Append the audio blob as a file with appropriate filename
    // Native platforms use m4a, web uses webm
    formData.append('audio', audioBlob, filename);

    const data = await fetchDictation('/api/dictate', {
      method: 'POST',
      body: formData,
      timeoutMs: DICTATE_TIMEOUT_MS,
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to transcribe audio', data.status || 400, data);
    }

    return {
      text: data.text || '',
      duration: data.duration,
      language: data.language,
    };
  },

  // Transcribe audio to text using Whisper (React Native — sends a file URI).
  // On React Native, Blob + FormData.append(name, blob, filename) does NOT
  // properly serialize binary data.  Instead we pass a { uri, type, name }
  // object which React Native's FormData natively understands.
  transcribeNative: async (fileUri, filename = 'recording.m4a', mimeType = 'audio/mp4') => {
    const formData = new FormData();
    formData.append('audio', {
      uri: fileUri,
      type: mimeType,
      name: filename,
    });

    const data = await fetchDictation('/api/dictate', {
      method: 'POST',
      body: formData,
      timeoutMs: DICTATE_TIMEOUT_MS,
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to transcribe audio', data.status || 400, data);
    }

    return {
      text: data.text || '',
      duration: data.duration,
      language: data.language,
    };
  },

  // Transcribe audio to text using base64 payload (simpler for iOS native).
  // POST /api/dictate/base64 with JSON body { audio: "base64...", format: "audio/mp4" }
  // Avoids multipart/form-data which is harder to use from native iOS (URLSession).
  transcribeBase64: async (base64Audio, format = 'audio/mp4') => {
    const data = await fetchDictation('/api/dictate/base64', {
      method: 'POST',
      body: { audio: base64Audio, format },
      timeoutMs: DICTATE_TIMEOUT_MS,
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to transcribe audio', data.status || 400, data);
    }

    return {
      text: data.text || '',
      duration: data.duration,
      language: data.language,
    };
  },
};
